import { FetchError, ShapeStream, type Message, type Row } from '@electric-sql/client';

type StreamState = 'idle' | 'connecting' | 'connected' | 'retrying' | 'paused' | 'stopped';

export class ManagedShapeStream<T extends Row<unknown> = Row<unknown>> {
  private stream: ShapeStream<T> | null = null;
  private streamAbort: AbortController | null = null;
  private state: StreamState = 'idle';
  private subscriptions = new Set<(messages: Message<T>[]) => void>();
  private reconnectTimeout?: ReturnType<typeof setTimeout>;
  private reconnectAttempts = 0;
  private canResume = false;
  private observingVisibility = false;
  private readonly onVisibilityChange = () => {
    if (document.hidden) {
      if (!this.stream && this.state !== 'retrying') return;
      if (this.reconnectTimeout !== undefined) clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
      this.stopStream();
      this.state = 'paused';
    } else if (this.state === 'paused') {
      void this.connect();
    }
  };
  private cursor?: { offset: ShapeStream<T>['lastOffset']; handle: string };

  constructor(private readonly config: {
    url: string;
    headers?: Record<string, string>;
  }) {}

  async connect(): Promise<void> {
    if (this.stream || this.state === 'connecting' || this.state === 'retrying') return;
    if (typeof document !== 'undefined') {
      if (!this.observingVisibility) {
        // Register before the SDK: abort the old generation before its async
        // pause can race a return to the foreground.
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.observingVisibility = true;
      }
      if (document.hidden) {
        this.state = 'paused';
        return;
      }
    }
    this.state = 'connecting';
    const abort = new AbortController();
    this.streamAbort = abort;
    try {
      const stream = new ShapeStream<T>({
        ...this.config,
        ...this.cursor,
        subscribe: true,
        liveSse: true,
        signal: abort.signal,
        onError: (error) => {
          if (this.streamAbort !== abort || abort.signal.aborted) return;
          const retryable = !(error instanceof FetchError)
            || error.status >= 500 || error.status === 408 || error.status === 429;
          console.warn('[ManagedShapeStream] Connection failed', JSON.stringify({
            status: error instanceof FetchError ? error.status : null,
            retryable,
            reconnectAttempts: this.reconnectAttempts,
          }));
          this.canResume = retryable;
          this.stopStream();
          this.state = 'stopped';
          if (retryable) this.scheduleReconnect();
        },
      });
      this.stream = stream;
      stream.subscribe((messages) => {
        if (this.streamAbort !== abort || abort.signal.aborted) return;
        this.state = 'connected';
        this.reconnectAttempts = 0;
        for (const callback of this.subscriptions) {
          try {
            callback(messages);
          } catch (error) {
            console.error('[ManagedShapeStream] Subscription callback failed', error);
          }
        }
      });
    } catch {
      if (this.streamAbort !== abort) return;
      this.stopStream();
      this.state = 'stopped';
      this.canResume = true;
      this.scheduleReconnect();
    }
  }

  subscribe(callback: (messages: Message<T>[]) => void): () => void {
    this.subscriptions.add(callback);
    if (this.state === 'idle') void this.connect();
    return () => { this.subscriptions.delete(callback); };
  }

  private stopStream(): void {
    const stream = this.stream;
    if (stream?.shapeHandle) {
      this.cursor = { offset: stream.lastOffset, handle: stream.shapeHandle };
    }
    const abort = this.streamAbort;
    this.streamAbort = null;
    this.stream = null;
    abort?.abort();
    stream?.unsubscribeAll();
  }

  resume(): void {
    if (this.state !== 'stopped' || !this.canResume) return;
    this.reconnectAttempts = 0;
    void this.connect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= 5) return;
    const delay = Math.min(1000 * 2 ** this.reconnectAttempts, 30000) + Math.random() * 1000;
    this.reconnectAttempts += 1;
    this.state = 'retrying';
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = undefined;
      this.state = 'idle';
      void this.connect();
    }, delay);
  }

  disconnect(): void {
    if (this.observingVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.observingVisibility = false;
    }
    if (this.reconnectTimeout !== undefined) clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = undefined;
    this.stopStream();
    this.state = 'idle';
    this.cursor = undefined;
    this.reconnectAttempts = 0;
    this.canResume = false;
    this.subscriptions.clear();
  }

  get connected(): boolean {
    return this.state === 'connected' && this.stream?.isConnected() === true;
  }
}
